import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/shadcn/button';
import { Input } from '@/components/shadcn/input';
import { Checkbox } from '@/components/shadcn/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/shadcn/form';
import { Alert, AlertDescription } from '@/components/shadcn/alert';
import { PasswordInput } from '@/components/auth/PasswordInput';
import { PasswordStrength } from '@/components/auth/PasswordStrength';
import { PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from '@/components/auth/constants';
import { openExternalUrl } from '@/utils/openExternalUrl';
import { authService } from '@/services/authService';

const registerSchema = z
  .object({
    name: z.string().min(1, 'Nama lengkap wajib diisi'),
    email: z.string().email('Format email tidak valid'),
    phone: z
      .string()
      .optional()
      .refine((val) => !val || /^08\d{8,13}$/.test(val), {
        message: 'Nomor HP harus dimulai 08 dan 10-15 digit',
      }),
    password: z.string().min(8, 'Password minimal 8 karakter'),
    confirmPassword: z.string(),
    acceptTerms: z.literal(true, {
      errorMap: () => ({ message: 'Anda harus menyetujui syarat & ketentuan' }),
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Password tidak cocok',
    path: ['confirmPassword'],
  });

type RegisterFormValues = z.infer<typeof registerSchema>;

let termsCheckboxId = 0;

export function RegisterForm() {
  const navigate = useNavigate();
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const termsId = `accept-terms-${++termsCheckboxId}`;

  const form = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      acceptTerms: undefined,
    },
  });

  const passwordValue = form.watch('password');

  const onSubmit = async (values: RegisterFormValues) => {
    setApiError(null);
    setIsSubmitting(true);
    try {
      await authService.registerCustomerPayment({
        name: values.name,
        email: values.email,
        phone: values.phone,
        password: values.password,
        confirmPassword: values.confirmPassword,
      });
      const email = encodeURIComponent(values.email.trim().toLowerCase());
      navigate(`/check-email?context=register&email=${email}`);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message =
          (error.response?.data as { message?: string })?.message ??
          'Terjadi kesalahan sistem, silakan coba lagi nanti';
        setApiError(message);
      } else if (error instanceof Error) {
        setApiError(error.message);
      } else {
        setApiError('Terjadi kesalahan sistem, silakan coba lagi nanti');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {apiError && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{apiError}</AlertDescription>
        </Alert>
      )}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nama Lengkap</FormLabel>
                <FormControl>
                  <Input placeholder="Nama sesuai identitas" autoComplete="name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input placeholder="nama@email.com" type="email" autoComplete="email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nomor HP (Opsional)</FormLabel>
                <FormControl>
                  <Input placeholder="08xxxxxxxxxx" type="tel" autoComplete="tel" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <PasswordInput placeholder="Minimal 8 karakter" autoComplete="new-password" {...field} />
                </FormControl>
                <PasswordStrength password={passwordValue} />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Konfirmasi Password</FormLabel>
                <FormControl>
                  <PasswordInput placeholder="Ulangi password" autoComplete="new-password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="acceptTerms"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-start gap-2">
                  <FormControl>
                    <Checkbox
                      id={termsId}
                      checked={field.value === true}
                      onCheckedChange={(checked) => field.onChange(checked === true)}
                      aria-label="Saya setuju dengan Syarat & Ketentuan dan Kebijakan Privasi Sporto"
                    />
                  </FormControl>
                  <label htmlFor={termsId} className="text-sm leading-snug text-muted-foreground">
                    Saya setuju dengan{' '}
                    <a
                      href={TERMS_OF_SERVICE_URL}
                      className="text-primary underline"
                      onClick={(event) => {
                        event.preventDefault();
                        void openExternalUrl(TERMS_OF_SERVICE_URL);
                      }}
                    >
                      Syarat & Ketentuan
                    </a>{' '}
                    dan{' '}
                    <a
                      href={PRIVACY_POLICY_URL}
                      className="text-primary underline"
                      onClick={(event) => {
                        event.preventDefault();
                        void openExternalUrl(PRIVACY_POLICY_URL);
                      }}
                    >
                      Kebijakan Privasi
                    </a>{' '}
                    Sporto.
                  </label>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            className="w-full bg-primary-500 hover:bg-primary-600"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Memproses...' : 'Daftar'}
          </Button>
        </form>
      </Form>
      {/* TODO(next-iteration): Google OAuth
      <Button variant="outline" onClick={handleGoogleLogin}>Lanjutkan dengan Google</Button>
      */}
    </div>
  );
}
